import { useState, useEffect } from "react";
import SectionHeader from "@/components/SectionHeader";
import { IMG, api, API } from "@/lib/api";
import { Link } from "react-router-dom";
import { Calendar, Music, PawPrint, HeartHandshake, Facebook, ArrowUpRight, CheckCircle2, Ticket, MapPin, Sparkles } from "lucide-react";

const FB_PAGE = "https://www.facebook.com/teamchampagneandsunshine";
const FB_JOINED_KEY = "cs_fb_joined";

const LA_TZ = "America/Los_Angeles";
const fmtWhen = (startIso, endIso) => {
  const start = new Date(startIso);
  const end = new Date(endIso);
  const day = start.toLocaleDateString("en-US", { timeZone: LA_TZ, weekday: "long", month: "long", day: "numeric" });
  const t = (d) => d.toLocaleTimeString("en-US", { timeZone: LA_TZ, hour: "numeric", minute: "2-digit" });
  return `${day} · ${t(start)} – ${t(end)}`;
};

const toSeminar = (e) => ({
  id: e.id,
  title: e.title,
  subtitle: e.subtitle,
  endsAt: e.ends_at,
  when: fmtWhen(e.starts_at, e.ends_at),
  location: [e.location_name, e.address].filter(Boolean).join(" · "),
  mapUrl: e.address ? `https://www.google.com/maps/search/?api=1&query=${encodeURIComponent([e.location_name, e.address].filter(Boolean).join(", "))}` : "",
  perks: e.highlights,
  desc: e.description,
  eventbrite: e.rsvp_url,
  image: e.has_image ? `${API}/events/${e.id}/image?v=${encodeURIComponent(e.updated_at)}` : "",
});

const isPast = (e) => new Date(e.endsAt).getTime() < Date.now();
const sortEvents = (list) => {
  const upcoming = list.filter((e) => !isPast(e)).sort((a, b) => new Date(a.endsAt) - new Date(b.endsAt));
  const past = list.filter(isPast).sort((a, b) => new Date(b.endsAt) - new Date(a.endsAt));
  return [...upcoming, ...past];
};

function PastEventNote() {
  return (
    <div className="bg-white p-8 md:p-10 h-full flex flex-col" data-testid="seminar-past-note">
      <p className="overline text-ocean">Event recap</p>
      <h4 className="mt-3 text-2xl md:text-3xl text-ocean" style={{ fontFamily: "Playfair Display, serif" }}>
        This event has passed, but there will be more!
      </h4>
      <p className="mt-4 text-soft text-sm leading-relaxed">
        Thank you to everyone who joined us. Follow our Facebook page to be first in line for the next one.
      </p>
      <a
        href={FB_PAGE}
        target="_blank"
        rel="noopener noreferrer"
        className="mt-8 inline-flex items-center justify-center px-6 py-4 text-sm font-medium tracking-wide w-full"
        style={{ backgroundColor: "#1e4d5d", color: "#fff" }}
        data-testid="seminar-past-follow"
      >
        <Facebook size={16} className="mr-2" /> Follow on Facebook <ArrowUpRight size={14} className="ml-2" />
      </a>
    </div>
  );
}

const CAUSES = [
  { Icon: Music, t: "Local Working Musicians", d: "We book, promote, and cheer on the LA artists who keep our community's soundtrack live." },
  { Icon: PawPrint, t: "Local LA Animal Shelters", d: "Supporting rescue, adoption, and care for LA's animals waiting for a home of their own." },
  { Icon: HeartHandshake, t: "KW Cares Foundation", d: "Helping fellow Keller Williams associates and their families through hardship and disaster." },
];

const fmtDate = (iso) => (iso ? new Date(iso).toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" }) : "");

function FbPostCard({ post }) {
  return (
    <a
      href={post.permalink_url || FB_PAGE}
      target="_blank"
      rel="noopener noreferrer"
      className="group bg-white flex flex-col overflow-hidden shadow-[0_18px_40px_-28px_rgba(30,77,93,0.5)] transition-[transform,box-shadow] duration-500 hover:-translate-y-1 hover:shadow-[0_26px_50px_-26px_rgba(30,77,93,0.6)]"
      data-testid={`fb-post-${post.id}`}
    >
      {post.full_picture && (
        <div className="overflow-hidden bg-[#e8dfcf]">
          <img src={post.full_picture} alt="" loading="lazy" className="w-full h-auto block transition-transform duration-700 group-hover:scale-105" />
        </div>
      )}
      <div className="p-6 flex flex-col flex-1">
        <p className="text-[11px] tracking-[0.2em] uppercase text-[#b8814f]">{fmtDate(post.created_time)}</p>
        {post.message && <p className="mt-3 text-sm text-soft leading-relaxed line-clamp-5 whitespace-pre-line">{post.message}</p>}
        <span className="mt-auto pt-5 inline-flex items-center text-sm text-ocean font-medium">
          <Facebook size={14} className="mr-2" /> View on Facebook <ArrowUpRight size={12} className="ml-1" />
        </span>
      </div>
    </a>
  );
}

function FbFallbackCard() {
  return (
    <a
      href={FB_PAGE}
      target="_blank"
      rel="noopener noreferrer"
      className="group block p-10 md:p-14 text-white shadow-[0_24px_60px_-30px_rgba(30,77,93,0.6)]"
      style={{ background: "linear-gradient(140deg, #1e4d5d 0%, #2c5865 60%, #b8814f 140%)" }}
      data-testid="fb-feed-fallback-card"
    >
      <Facebook size={34} className="text-[#e8c79a]" />
      <p className="mt-6 text-2xl md:text-3xl text-white" style={{ fontFamily: "Playfair Display, serif" }}>
        See our latest posts on Facebook
      </p>
      <p className="mt-3 text-white/80 text-sm">Event photos, live music nights, open houses and team news.</p>
      <span className="mt-8 inline-flex items-center text-sm text-[#e8c79a] group-hover:translate-x-1 transition-transform">
        Visit our page <ArrowUpRight size={14} className="ml-2" />
      </span>
    </a>
  );
}

function FacebookFeed() {
  const [feed, setFeed] = useState(null);

  useEffect(() => {
    api.get("/facebook/feed").then((r) => setFeed(r.data)).catch(() => setFeed({ posts: [] }));
  }, []);

  return (
    <section className="section bg-linen" data-testid="community-fb-feed">
      <div className="container-edge">
        <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-8">
          <div className="max-w-2xl">
            <p className="overline">Live from Facebook</p>
            <h2 className="mt-4 text-3xl md:text-4xl lg:text-5xl text-ocean leading-tight" style={{ fontFamily: "Playfair Display, serif" }}>
              Stay in the know with community events, live music, and other announcements.
            </h2>
          </div>
          <a href={FB_PAGE} target="_blank" rel="noopener noreferrer" className="btn-primary inline-flex items-center self-start md:self-auto shrink-0" data-testid="fb-feed-follow">
            <Facebook size={16} className="mr-2" /> Follow us on Facebook <ArrowUpRight size={14} className="ml-2" />
          </a>
        </div>

        <div className="mt-12">
          {feed === null && (
            <div className="grid gap-8 sm:grid-cols-2 lg:grid-cols-3" data-testid="fb-feed-loading">
              {[0, 1, 2].map((n) => <div key={`sk-${n}`} className="h-80 bg-white/70 animate-pulse" />)}
            </div>
          )}
          {feed?.posts?.length > 0 && (
            <div className="grid gap-8 sm:grid-cols-2 lg:grid-cols-3 items-start" data-testid="fb-feed-grid">
              {feed.posts.slice(0, 6).map((p) => <FbPostCard key={p.id} post={p} />)}
            </div>
          )}
          {feed && !feed.posts?.length && <FbFallbackCard />}
        </div>
      </div>
    </section>
  );
}

export default function Community() {
  const [fbJoined, setFbJoined] = useState(false);
  const [seminars, setSeminars] = useState(null);

  useEffect(() => {
    api.get("/events").then((r) => setSeminars(r.data.map(toSeminar))).catch(() => setSeminars([]));
  }, []);

  useEffect(() => {
    setFbJoined(localStorage.getItem(FB_JOINED_KEY) === "true");
  }, []);

  const handleJoinFb = () => {
    localStorage.setItem(FB_JOINED_KEY, "true");
    setFbJoined(true);
    window.open(FB_PAGE, "_blank", "noopener,noreferrer");
  };

  return (
    <div data-testid="community-page">
      <section className="relative h-[60vh] min-h-[420px] overflow-hidden">
        <img src={IMG.lifestyle2} alt="Community" className="absolute inset-0 w-full h-full object-cover img-warm" />
        <div className="absolute inset-0 bg-gradient-to-b from-black/30 to-black/65" />
        <div className="relative h-full container-edge flex items-end pb-16">
          <div className="text-white max-w-2xl">
            <p className="overline text-[#e8c79a]">Events</p>
            <h1 className="hero-headline mt-4 text-5xl md:text-6xl lg:text-7xl text-white" style={{ fontFamily: "Playfair Display, serif" }}>
              Real estate is people work.
            </h1>
            <p className="mt-6 text-lg text-white/90 max-w-xl font-light">
              The neighborhoods we serve are the neighborhoods we live in. Here's where we show
              up — seminars, gatherings, and the LA community beyond the closing table.
            </p>
          </div>
        </div>
      </section>

      <FacebookFeed />

      {/* MONTHLY SEMINARS — Facebook-first soft gate */}
      <section
        className="section"
        style={{ backgroundColor: "#1e4d5d" }}
        data-testid="community-seminars"
      >
        <div className="container-edge">
          <p className="overline" style={{ color: "#e8c79a" }}>Monthly Seminars</p>
          <h2
            className="mt-4 text-4xl md:text-5xl lg:text-6xl text-white"
            style={{ fontFamily: "Playfair Display, serif" }}
          >
            Learn with us — <em className="not-italic" style={{ color: "#e8c79a", fontStyle: "italic" }}>free every month</em>.
          </h2>
          <p className="mt-6 text-white/85 max-w-2xl leading-relaxed">
            Every month, we host a free educational seminar for buyers, sellers, and investors in
            the Champagne &amp; Sunshine community. To reserve your seat, follow our Facebook page
            first — that's where we drop early access, replays, and community-only invites.
          </p>

          {seminars === null && <div className="mt-12 h-64 bg-white/5 animate-pulse" data-testid="seminars-loading" />}
          {seminars && !seminars.some((e) => !isPast(e)) && (
            <p className="mt-10 inline-flex items-center gap-2 text-[#e8c79a]" data-testid="seminar-none-upcoming">
              <Calendar size={16} /> New events are on the way. Follow us on Facebook for the next date.
            </p>
          )}
          {sortEvents(seminars || []).map((s, idx) => {
            const past = isPast(s);
            return (
            <div
              key={s.id}
              className={`mt-12 border p-8 md:p-12 ${past ? "bg-white/[0.03] border-white/10 opacity-80" : "bg-white/5 border-white/15 backdrop-blur-sm"}`}
              data-testid={`seminar-${s.id}`}
              data-past={past}
            >
              <div className="grid grid-cols-1 md:grid-cols-12 gap-10">
                <div className="md:col-span-7 text-white">
                  {s.image && (
                    <img src={s.image} alt={s.title} className="mb-8 w-full max-h-[420px] object-cover" data-testid="seminar-image" />
                  )}
                  {past ? (
                    <span className="inline-flex items-center gap-2 px-3 py-1 text-[11px] tracking-[0.2em] uppercase bg-white/10 text-white/80" data-testid="seminar-past-badge">
                      Past event
                    </span>
                  ) : (
                    <p className="overline" style={{ color: "#e8c79a" }}>{idx === 0 ? "Next up" : "Coming up"}</p>
                  )}
                  <h3
                    className="mt-3 text-3xl md:text-4xl text-white"
                    style={{ fontFamily: "Playfair Display, serif" }}
                    data-testid="seminar-title"
                  >
                    {s.title}
                  </h3>
                  {s.subtitle && (
                    <p className="mt-2 text-lg text-white/90 italic" style={{ fontFamily: "Playfair Display, serif" }}>
                      {s.subtitle}
                    </p>
                  )}
                  <div className="mt-6 space-y-3 text-sm text-white/85">
                    <div className="flex items-start gap-3">
                      <Calendar size={16} className="text-[#e8c79a] mt-0.5 shrink-0" />
                      <span data-testid="seminar-date">{s.when}</span>
                    </div>
                    {s.location && (
                      <div className="flex items-start gap-3">
                        <MapPin size={16} className="text-[#e8c79a] mt-0.5 shrink-0" />
                        <span data-testid="seminar-location">
                          {s.location}
                          {s.mapUrl && (
                            <a href={s.mapUrl} target="_blank" rel="noopener noreferrer" className="ml-2 underline text-[#e8c79a] hover:text-white" data-testid="seminar-map-link">
                              Map
                            </a>
                          )}
                        </span>
                      </div>
                    )}
                    {s.perks && (
                      <div className="flex items-start gap-3">
                        <Sparkles size={16} className="text-[#e8c79a] mt-0.5 shrink-0" />
                        <span data-testid="seminar-perks">{s.perks}</span>
                      </div>
                    )}
                  </div>
                  {s.desc && <p className="mt-6 text-white/85 leading-relaxed whitespace-pre-line">{s.desc}</p>}
                </div>

                {/* Soft gate card */}
                <div className="md:col-span-5">
                  {past ? <PastEventNote /> : (
                  <div className="bg-white p-8 md:p-10 h-full flex flex-col">
                    {!fbJoined ? (
                      <>
                        <p className="overline text-ocean">Step 1 · Join our community</p>
                        <h4
                          className="mt-3 text-2xl md:text-3xl text-ocean"
                          style={{ fontFamily: "Playfair Display, serif" }}
                        >
                          Follow us on Facebook first.
                        </h4>
                        <p className="mt-4 text-soft text-sm leading-relaxed">
                          Our seminars are hosted through our Facebook community. Give the page a
                          follow so you'll get event reminders, live replays, and last-minute
                          seat drops — then we'll unlock the RSVP link below.
                        </p>
                        <button
                          onClick={handleJoinFb}
                          className="mt-8 inline-flex items-center justify-center px-6 py-4 text-sm font-medium tracking-wide transition-all duration-300 w-full"
                          style={{ backgroundColor: "#1e4d5d", color: "#fff" }}
                          data-testid="seminar-fb-join"
                        >
                          <Facebook size={16} className="mr-2" />
                          Follow on Facebook
                          <ArrowUpRight size={14} className="ml-2" />
                        </button>
                        <p className="mt-4 text-xs text-soft">
                          The RSVP link appears here after you follow.
                        </p>
                      </>
                    ) : (
                      <>
                        <div className="flex items-center gap-2 text-ocean">
                          <CheckCircle2 size={18} className="text-sand" />
                          <p className="overline text-ocean">Step 2 · Reserve your seat</p>
                        </div>
                        <h4
                          className="mt-3 text-2xl md:text-3xl text-ocean"
                          style={{ fontFamily: "Playfair Display, serif" }}
                        >
                          You're in — grab your ticket.
                        </h4>
                        <p className="mt-4 text-soft text-sm leading-relaxed">
                          Thanks for following the page. Seats are limited — reserve yours on
                          Eventbrite. See you there.
                        </p>
                        <a
                          href={s.eventbrite || "/contact?interest=events"}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="mt-8 inline-flex items-center justify-center px-6 py-4 text-sm font-medium tracking-wide transition-all duration-300 w-full"
                          style={{ backgroundColor: "#d4a373", color: "#1a2a30" }}
                          data-testid="seminar-eventbrite"
                        >
                          <Ticket size={16} className="mr-2" />
                          {s.eventbrite.includes("eventbrite") ? "Reserve on Eventbrite" : "Reserve your seat"}
                          <ArrowUpRight size={14} className="ml-2" />
                        </a>
                        <button
                          onClick={() => {
                            localStorage.removeItem(FB_JOINED_KEY);
                            setFbJoined(false);
                          }}
                          className="mt-4 text-xs text-soft underline hover:text-ocean transition-colors self-start"
                          data-testid="seminar-fb-reset"
                        >
                          Not following yet? Restart step 1
                        </button>
                      </>
                    )}
                  </div>
                  )}
                </div>
              </div>
            </div>
            );
          })}
        </div>
      </section>

      <section className="section bg-linen" data-testid="community-causes">
        <SectionHeader overline="Causes we support" title="Where our hours go." />
        <div className="mt-16 grid grid-cols-1 md:grid-cols-3 gap-8">
          {CAUSES.map(({ Icon, t, d }, i) => (
            <div key={t} className="bg-paper p-10 border border-soft" data-testid={`cause-${i}`}>
              <Icon size={28} className="text-sand" strokeWidth={1.25} />
              <h3 className="mt-6 text-2xl" style={{ fontFamily: "Playfair Display, serif" }}>{t}</h3>
              <p className="mt-3 text-soft text-sm">{d}</p>
            </div>
          ))}
        </div>
      </section>

      <section className="section">
        <div className="grid grid-cols-1 md:grid-cols-12 gap-8 items-center">
          <div className="md:col-span-8">
            <h2 className="text-4xl md:text-5xl" style={{ fontFamily: "Playfair Display, serif" }}>
              Want to be on the list?
            </h2>
            <p className="mt-4 text-soft max-w-xl">Drop your email and we'll send invites to the next gathering — that's it. No spam.</p>
          </div>
          <div className="md:col-span-4 md:text-right">
            <Link to="/contact" className="btn-primary" data-testid="community-final-cta">Get on the list</Link>
          </div>
        </div>
      </section>
    </div>
  );
}
